import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Sparkles, Video, Globe, QrCode, ArrowRight } from 'lucide-react';

type Tool =
  | 'ai-transcribe' | 'video-subtitles' | 'video' | 'audio-pro' | 'youtube' | 'instagram'
  | 'ai-ideas' | 'ai-captions' | 'ig-audit' | 'ig-compare' | 'ai-product'
  | 'website-report' | 'web-intel' | 'ai-blog'
  | 'qr-studio';

interface HomeDashboardProps {
  onSelectTool: (tool: Tool) => void;
}

// Mirrors the sidebar's team groups and order
const categories = [
  {
    id: 'video',
    title: 'Video Team',
    tagline: 'Transcribe. Subtitle. Convert.',
    description: 'Transcribe audio, subtitle videos, convert media, download from YouTube & Instagram',
    icon: Video,
    gradient: 'from-[#e4c184] via-[#d4a855] to-[#b8893a]',
    accent: '#216974',
    accentLight: 'rgba(33,105,116,0.15)',
    glow: '0 20px 60px rgba(228,193,132,0.35)',
    tools: ['ai-transcribe', 'video-subtitles', 'video', 'audio-pro', 'youtube', 'instagram'] as Tool[],
    chips: ['Transcribe', 'Subtitles', 'Video & Media', 'Audio Pro', 'YouTube DL'],
    emoji: '🎬',
  },
  {
    id: 'social',
    title: 'Social Media Team',
    tagline: 'Plan. Create. Analyse.',
    description: 'Content calendar, captions, Instagram audit & compare, product copy',
    icon: Sparkles,
    gradient: 'from-[#216974] via-[#1a5560] to-[#0f3d45]',
    accent: '#54c4b6',
    accentLight: 'rgba(84,196,182,0.15)',
    glow: '0 20px 60px rgba(33,105,116,0.35)',
    tools: ['ai-ideas', 'ai-captions', 'ig-audit', 'ig-compare', 'ai-product'] as Tool[],
    chips: ['Content Calendar', 'Caption Gen', 'IG Audit', 'IG Compare', 'Product Copy'],
    emoji: '✨',
  },
  {
    id: 'web',
    title: 'Web & SEO Team',
    tagline: 'Audit. Research. Write.',
    description: 'Website reports, site intelligence and blog writing',
    icon: Globe,
    gradient: 'from-[#54c4b6] via-[#3aada0] to-[#216974]',
    accent: '#e4c184',
    accentLight: 'rgba(228,193,132,0.15)',
    glow: '0 20px 60px rgba(84,196,182,0.35)',
    tools: ['website-report', 'web-intel', 'ai-blog'] as Tool[],
    chips: ['Website Report', 'Site Intelligence', 'Blog Writer'],
    emoji: '🌐',
  },
  {
    id: 'extras',
    title: 'Extras',
    tagline: 'Handy utilities.',
    description: 'QR code studio',
    icon: QrCode,
    gradient: 'from-[#1a5560] via-[#216974] to-[#2a8a9a]',
    accent: '#54c4b6',
    accentLight: 'rgba(84,196,182,0.15)',
    glow: '0 20px 60px rgba(26,85,96,0.35)',
    tools: ['qr-studio'] as Tool[],
    chips: ['QR Studio'],
    emoji: '🧰',
  },
];

const allTools: { id: Tool; label: string; category: string }[] = [
  { id: 'ai-transcribe',   label: 'Transcribe',           category: 'Video' },
  { id: 'video-subtitles', label: 'Video Subtitles',      category: 'Video' },
  { id: 'video',           label: 'Video & Media',        category: 'Video' },
  { id: 'audio-pro',       label: 'Audio Pro',            category: 'Video' },
  { id: 'youtube',         label: 'YouTube Downloader',   category: 'Video' },
  { id: 'instagram',       label: 'Instagram Downloader', category: 'Video' },
  { id: 'ai-ideas',        label: 'Content Calendar',     category: 'Social Media' },
  { id: 'ai-captions',     label: 'Caption Generator',    category: 'Social Media' },
  { id: 'ig-audit',        label: 'Instagram Audit',      category: 'Social Media' },
  { id: 'ig-compare',      label: 'Instagram Compare',    category: 'Social Media' },
  { id: 'ai-product',      label: 'Product Copy',         category: 'Social Media' },
  { id: 'website-report',  label: 'Website Report',       category: 'Web & SEO' },
  { id: 'web-intel',       label: 'Site Intelligence',    category: 'Web & SEO' },
  { id: 'ai-blog',         label: 'Blog Writer',          category: 'Web & SEO' },
  { id: 'qr-studio',       label: 'QR Studio',            category: 'Extras' },
];

const HomeDashboard: React.FC<HomeDashboardProps> = ({ onSelectTool }) => {
  const [search, setSearch] = useState('');
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  const filtered = search.trim()
    ? allTools.filter(t =>
        t.label.toLowerCase().includes(search.toLowerCase()) ||
        t.category.toLowerCase().includes(search.toLowerCase())
      )
    : [];

  return (
    <div className="h-full flex flex-col items-center justify-center py-6 px-4">
      {/* Logo */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="text-center mb-6"
      >
        <h1 className="text-5xl font-black tracking-tight mb-1.5 leading-none select-none">
          <span style={{ color: '#54c4b6' }}>aadi</span>
          <span style={{ color: '#e4c184' }}>N</span>
        </h1>
        <p className="text-gray-400 text-sm font-medium tracking-wide">The beginning of unlimited</p>
      </motion.div>

      {/* Search */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="w-full max-w-xl mb-7 relative"
      >
        <div className="relative">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search tools..."
            className="w-full pl-11 pr-4 py-3 text-sm rounded-2xl border border-gray-200 shadow-md focus:outline-none focus:ring-2 focus:ring-[#54c4b6] focus:border-transparent bg-white/90 backdrop-blur-sm transition-all"
          />
        </div>
        <AnimatePresence>
          {filtered.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              className="absolute z-20 left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden"
            >
              {filtered.map(tool => (
                <button
                  key={tool.id}
                  onClick={() => { setSearch(''); onSelectTool(tool.id); }}
                  className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-[#54c4b6]/10 transition-colors text-left"
                >
                  <span className="font-medium text-gray-800 text-sm">{tool.label}</span>
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full shrink-0 ml-4">{tool.category}</span>
                </button>
              ))}
            </motion.div>
          )}
          {search.trim() && filtered.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute z-20 left-0 right-0 mt-2 bg-white rounded-xl shadow border border-gray-100 px-4 py-3 text-gray-400 text-sm"
            >
              No tools found for &ldquo;{search}&rdquo;
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Category Cards — 2x2 */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.15 }}
        className="w-full max-w-3xl grid grid-cols-2 gap-4"
      >
        {categories.map((cat, i) => {
          const Icon = cat.icon;
          const isHovered = hoveredCard === cat.id;
          return (
            <motion.button
              key={cat.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 + i * 0.07, duration: 0.4, ease: 'easeOut' }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onSelectTool(cat.tools[0])}
              onMouseEnter={() => setHoveredCard(cat.id)}
              onMouseLeave={() => setHoveredCard(null)}
              className={`relative overflow-hidden rounded-2xl text-left transition-all duration-300 ${isHovered ? 'scale-[1.025]' : 'scale-100'}`}
              style={{ boxShadow: isHovered ? cat.glow : '0 4px 20px rgba(0,0,0,0.08)' }}
            >
              {/* Gradient background */}
              <div className={`absolute inset-0 bg-gradient-to-br ${cat.gradient} transition-opacity duration-300`} />

              {/* Decorative circle */}
              <div
                className="absolute -top-6 -right-6 w-28 h-28 rounded-full opacity-20 transition-all duration-500"
                style={{ background: cat.accent, transform: isHovered ? 'scale(1.3)' : 'scale(1)' }}
              />
              <div
                className="absolute -bottom-8 -left-4 w-20 h-20 rounded-full opacity-10 transition-all duration-500"
                style={{ background: cat.accent, transform: isHovered ? 'scale(1.5)' : 'scale(1)' }}
              />

              {/* Shimmer line at top */}
              <div
                className="absolute top-0 left-0 right-0 h-[2px] rounded-t-2xl transition-opacity duration-300"
                style={{ background: `linear-gradient(90deg, transparent, ${cat.accent}, transparent)`, opacity: isHovered ? 1 : 0.4 }}
              />

              {/* Content */}
              <div className="relative z-10 p-5">
                {/* Top row: icon + emoji + tool count */}
                <div className="flex items-start justify-between mb-3">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center transition-transform duration-300"
                    style={{
                      background: 'rgba(255,255,255,0.15)',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      transform: isHovered ? 'rotate(-6deg) scale(1.1)' : 'rotate(0deg) scale(1)',
                    }}
                  >
                    <Icon size={20} className="text-white" />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] font-semibold text-white/70 bg-white/10 px-2.5 py-1 rounded-full border border-white/20">
                      {cat.tools.length} tools
                    </span>
                  </div>
                </div>

                {/* Title + tagline */}
                <h3 className="font-black text-white text-lg leading-tight mb-0.5">{cat.title}</h3>
                <p className="text-white/60 text-[11px] font-semibold uppercase tracking-widest mb-3">{cat.tagline}</p>

                {/* Tool chips */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {cat.chips.map((chip, ci) => (
                    <span
                      key={ci}
                      className="text-[10px] font-semibold px-2.5 py-1 rounded-full border transition-all duration-300"
                      style={{
                        background: isHovered ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.10)',
                        borderColor: 'rgba(255,255,255,0.2)',
                        color: 'rgba(255,255,255,0.9)',
                      }}
                    >
                      {chip}
                    </span>
                  ))}
                </div>

                {/* CTA row */}
                <div
                  className="flex items-center gap-1.5 transition-all duration-300"
                  style={{ opacity: isHovered ? 1 : 0.6, transform: isHovered ? 'translateX(4px)' : 'translateX(0)' }}
                >
                  <span className="text-xs font-bold text-white">Open tools</span>
                  <ArrowRight size={13} className="text-white" />
                </div>
              </div>
            </motion.button>
          );
        })}
      </motion.div>
    </div>
  );
};

export default HomeDashboard;
